import type { ConfigurationItemsByBlock } from '../../workspace/operationsContext'
import type {
  ConfiguredEnumLiteral,
  EnumDefinition,
} from '../enumLiterals/types'

export type EnumConflictType = 'none' | 'definition' | 'literal' | 'both'

/**
 * Checks whether a non-annulled enum definition or literals with matching
 * names already exist in the destination block. Returns 'both' when both
 * conflict, 'definition' or 'literal' when only one does, and 'none' otherwise.
 *
 * Literal conflicts are scoped to `transferringLiteralNames` — only literals
 * whose names are being transferred are considered a conflict.
 */
export const detectEnumTransferConflict = ({
  configuredEnums,
  enumName,
  destinationBlock,
  transferringLiteralNames,
}: {
  configuredEnums: ConfigurationItemsByBlock<EnumDefinition>
  enumName: string
  destinationBlock: string
  transferringLiteralNames: string[]
}): EnumConflictType => {
  const destinationBlockEnums = configuredEnums.get(destinationBlock)
  if (!destinationBlockEnums) {
    return 'none'
  }

  let hasDefinitionConflict = false
  let hasLiteralConflict = false

  for (const enumsForLayer of destinationBlockEnums.values()) {
    const enumDef = enumsForLayer.get(enumName)
    if (!enumDef || enumDef.annulOperations.length > 0) {
      continue
    }

    if (enumDef.internalOperation) {
      hasDefinitionConflict = true
    }

    const hasNonAnnulledLiteralConflicts = enumDef.literals.some(
      (lit): lit is ConfiguredEnumLiteral =>
        lit.type === 'configured' &&
        lit.annulOperations.length === 0 &&
        transferringLiteralNames.includes(
          lit.internalOperation.entity?.name ?? '',
        ),
    )
    if (hasNonAnnulledLiteralConflicts) {
      hasLiteralConflict = true
    }
  }

  if (hasDefinitionConflict && hasLiteralConflict) {
    return 'both'
  }
  if (hasDefinitionConflict) {
    return 'definition'
  }
  if (hasLiteralConflict) {
    return 'literal'
  }
  return 'none'
}
